import React, { Dispatch, useState, useMemo } from 'react';

import {
    getCurrentQuarter,
    getPriorQuarters,
    getPeriodLabelsForQuarterDateRange,
    locale,
} from '@trqk/utils';

import { DropdownNestedOption, DropdownWrapper, DropdownOption } from '..';

interface QuarterSelectorProps {
    borderConfig?: DropdownBorderConfig;
    handleClick?(): void;
    height: number;
    initialQuarterIndex?: number;
    now: Date;
    priorQuarters: number;
    setDateRange: Dispatch<DateRange>;
    setSelectedLabel: Dispatch<string>;
    width: number;
}

export const QuarterSelector = (props: QuarterSelectorProps): JSX.Element => {
    const {
        borderConfig,
        handleClick,
        height,
        initialQuarterIndex = 0,
        now,
        priorQuarters,
        setDateRange,
        setSelectedLabel,
        width,
    } = props;

    const quarterOptions = useMemo((): DropdownOption<DateRange>[] => {
        const priorQuarterDateRanges = getPriorQuarters(now, priorQuarters);

        const priorQuarterOptions = priorQuarterDateRanges.map(
            (quarter: DateRange): DropdownOption<DateRange> => {
                const periodLabels =
                    getPeriodLabelsForQuarterDateRange(quarter);

                return {
                    label: periodLabels.shortestDateLabel,
                    value: quarter,
                };
            },
        );

        const currentQuarterOption = {
            label: locale.enUS.DATE_RANGES.CURRENT_QUARTER,
            value: getCurrentQuarter(now),
        };

        return [currentQuarterOption, ...priorQuarterOptions.reverse()];
    }, [now]);

    const initialOption = quarterOptions[initialQuarterIndex];
    const [isExpanded, setIsExpanded] = useState(true);
    const [selectedQuarterLabel, setSelectedQuarterLabel] = useState(
        initialOption.label,
    );

    return (
        <DropdownNestedOption height={height} label="Quarter" width={width}>
            <DropdownWrapper
                borderConfig={borderConfig}
                closeOnClickOutside={false}
                disabled={false}
                height={height}
                isExpanded={isExpanded}
                selectedLabel={selectedQuarterLabel}
                setIsExpanded={setIsExpanded}
                showToggle={false}
                width={width}
            >
                {quarterOptions.map(
                    (option: DropdownOption<DateRange>): JSX.Element => (
                        <DropdownOption
                            key={option.label}
                            option={option}
                            handleChange={(
                                nextOption: DropdownOption<DateRange>,
                            ): void => {
                                const value = nextOption.value as DateRange;

                                setDateRange(value);
                                setSelectedQuarterLabel(nextOption.label);
                                setSelectedLabel(nextOption.label);
                                setIsExpanded(false);

                                if (handleClick) {
                                    handleClick();
                                }
                            }}
                            height={height}
                        />
                    ),
                )}
            </DropdownWrapper>
        </DropdownNestedOption>
    );
};
